/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, {
  ComponentPropsWithRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useLocation } from '@reach/router';
import { ThemeUIStyleObject, useThemeUI } from 'theme-ui';

import { useMergedRef } from './useMergedRef';
import { useBlockedDocument } from './useBlockedDocument';
import {
  AnimationState,
  useAnimationStateSelector,
  usePageTransitionActions,
  usePageTransitionDispatch,
} from './PageTransition';

export interface OverlayProps extends ComponentPropsWithRef<'div'> {
  /**
   * By default, the overlay will block pointer events and scrolling
   * for elements visually 'underneath' the overlay. Set this to `true`
   * to allow those pointer events.
   */
  allowPointerEvents?: boolean;
  sx?: ThemeUIStyleObject;
  modalId?: string;
}

/**
 * `Overlay` covers the viewport with a dark, semi-transparent color
 * and displays its content on top of the background. It also
 * blocks scrolling and pointer events for the content underneath,
 * unless `allowPointerEvents` is `true`.
 */
export const Overlay = forwardRef(
  (
    {
      allowPointerEvents = false,
      children,
      style,
      modalId,
      sx,
      ...props
    }: OverlayProps,
    forwardedRef: OverlayProps['ref'],
  ): React.JSX.Element => {
    const location = useLocation();
    const actions = usePageTransitionActions();
    const dispatch = usePageTransitionDispatch();
    const { theme } = useThemeUI();
    const variantStyles = theme.layout?.overlay || {};

    const enterLocation = useRef(location);
    const blockedDocRef = useBlockedDocument(!allowPointerEvents);
    const setRef = useMergedRef(forwardedRef, blockedDocRef);

    const ctrlIds = useRef(new Set<number>());
    const [transition, ref] = useSpring((_, ctrl) => {
      ctrlIds.current.add(ctrl.id);
      return {
        backgroundColor: theme?.colors?.transparent,
      };
    });

    useEffect(() => {
      const ctrlIds_ = ctrlIds.current;

      ctrlIds_.forEach((animationId) => {
        dispatch(
          actions.add({
            animationId,
            animationState: AnimationState.SHOULD_ENTER,
          }),
        );
      });

      return () => {
        ctrlIds_.forEach((animationId) => {
          dispatch(actions.remove(animationId));
        });
      };
    }, [actions, dispatch, ref]);

    const animationState = useAnimationStateSelector([...ctrlIds.current]);

    useLayoutEffect(() => {
      switch (animationState) {
        case AnimationState.SHOULD_ENTER: {
          enterLocation.current = location;
          ref.start({
            backgroundColor:
              modalId === 'main-menu'
                ? theme?.colors?.background
                : theme?.colors?.backgroundOverlay,
            onStart: (_, ctrl) => {
              dispatch(
                actions.set({
                  animationId: ctrl.id,
                  animationState: AnimationState.IS_ENTERING,
                }),
              );
            },
            onRest: (_, ctrl) => {
              dispatch(
                actions.set({
                  animationId: ctrl.id,
                  animationState: AnimationState.DID_ENTER,
                }),
              );
            },
          });
          break;
        }
        case AnimationState.SHOULD_LEAVE: {
          ref.start({
            backgroundColor: theme?.colors?.transparent,
            delay: 250,
            immediate: enterLocation.current !== location,
            onStart: (_, ctrl) => {
              dispatch(
                actions.set({
                  animationId: ctrl.id,
                  animationState: AnimationState.IS_LEAVING,
                }),
              );
            },
            onRest: (_, ctrl) => {
              dispatch(
                actions.set({
                  animationId: ctrl.id,
                  animationState: AnimationState.DID_LEAVE,
                }),
              );
            },
          });
          break;
        }
      }
    }, [actions, animationState, dispatch, location, ref, theme.colors]);

    return (
      <animated.div
        {...props}
        ref={setRef}
        style={
          {
            ...variantStyles,
            ...sx,
            pointerEvents: allowPointerEvents ? 'none' : 'auto',
            ...transition,
            ...style,
          } as React.CSSProperties
        }
      >
        {children}
      </animated.div>
    );
  },
);
Overlay.displayName = 'Overlay';
